<template>
  <PreAuthPage class="onb" card-class="onb__card">
    <section class="sh-card__head">
      <LogoTile :branding="branding" :alt="tenantName" :size="48" />
      <h1 class="sh-card__title">{{ headTitle }}</h1>
      <p class="sh-card__subtitle">{{ headSubtitle }}</p>
      <p v-if="email && phase === 'form'" class="sh-whoami">
        {{ t("common.signedInAs") }} <b>{{ email }}</b>
      </p>
    </section>

    <!-- ── Verifying ─────────────────────────────────────────────────────
         The backend drives a real browser through eCert's Blazor app. It is
         genuinely slow, so the wait is named honestly rather than hidden
         behind a spinner that looks stuck. -->
    <div v-if="phase === 'verifying'" class="onb__wait" role="status" aria-live="polite">
      <v-progress-circular indeterminate size="34" width="3" color="primary" />
      <p class="onb__wait-title">{{ t("onboarding.verifying") }}</p>
      <p class="onb__wait-body">{{ t("onboarding.verifyingBody") }}</p>
    </div>

    <!-- ── Pick a business ───────────────────────────────────────────────
         A consultant or a group login legitimately edits several. Choosing
         for them would hand somebody the wrong farm. -->
    <div v-else-if="phase === 'choose'" class="onb__choose">
      <p class="onb__choose-lead">{{ t("onboarding.chooseLead") }}</p>
      <div class="sh-rows">
        <button
          v-for="c in candidates"
          :key="c.externalBusinessId"
          type="button"
          class="sh-row onb__choice"
          :disabled="busy"
          @click="submit(c.externalBusinessId)"
        >
          <span class="sh-row__label">
            <span class="onb__choice-name">{{ c.businessName }}</span>
            <small v-if="c.referenceNumber" class="onb__choice-ref">{{ c.referenceNumber }}</small>
          </span>
          <v-icon size="16">chevron_right</v-icon>
        </button>
      </div>
      <p v-if="error" class="sh-card__error" role="alert">
        <v-icon size="15">error_outline</v-icon><span>{{ error }}</span>
      </p>
    </div>

    <!-- ── Needs a human ─────────────────────────────────────────────────
         Never a dead end: the reference is what support works from. -->
    <div v-else-if="phase === 'review'" class="onb__review" role="status">
      <v-icon size="20" color="info">schedule</v-icon>
      <div>
        <p class="onb__review-title">{{ t("onboarding.reviewTitle") }}</p>
        <p class="onb__review-body">{{ isCompany ? t("onboarding.company.reviewBody") : t("onboarding.reviewBody") }}</p>
        <p class="onb__ref">{{ reference }}</p>
      </div>
    </div>

    <!-- PAC-1374: a WMS front door (signupForm "COMPANY") asks for the company
         instead of the farm. Same frame, same review panel, same way out. No
         AgriOps step here — that is the farm form's identity check. -->
    <template v-else-if="isCompany">
      <CompanySignupForm @review="onCompanyReview" />
      <p class="onb__foot">{{ t("onboarding.company.footNote") }}</p>
      <v-btn class="onb__switch" variant="text" size="small" block @click="session.signOut()">{{
        t("auth.differentAccountSignIn")
      }}</v-btn>
    </template>

    <!-- ── The farm form ─────────────────────────────────────────────────-->
    <template v-else>
      <p v-if="error" class="sh-card__error" role="alert">
        <v-icon size="15">error_outline</v-icon><span>{{ error }}</span>
      </p>

      <form class="onb__form" @submit.prevent="submit()">
        <v-text-field
          v-model="farmName"
          :label="t('onboarding.farmName')"
          :hint="t('onboarding.farmNameHint')"
          persistent-hint
          variant="outlined"
          density="comfortable"
          autocomplete="organization"
          autofocus
          :disabled="busy"
        />

        <!-- The eCert block. This is the identity check, so it leads visually —
             but it is never mandatory: a grower who does not have their login
             to hand must still be able to get in and connect later. -->
        <div class="onb__ecert">
          <div class="onb__ecert-head">
            <v-icon size="16" color="primary">verified_user</v-icon>
            <span>{{ t("onboarding.ecertTitle") }}</span>
          </div>
          <p class="onb__ecert-blurb">{{ t("onboarding.ecertBlurb") }}</p>

          <v-text-field
            v-model="username"
            :label="t('onboarding.ecertUsername')"
            variant="outlined"
            density="compact"
            autocomplete="off"
            :disabled="busy"
            hide-details
            class="onb__ecert-field"
          />
          <v-text-field
            v-model="password"
            :label="t('onboarding.ecertPassword')"
            :type="showPassword ? 'text' : 'password'"
            :append-inner-icon="showPassword ? 'visibility_off' : 'visibility'"
            variant="outlined"
            density="compact"
            autocomplete="off"
            :disabled="busy"
            hide-details
            class="onb__ecert-field"
            @click:append-inner="showPassword = !showPassword"
          />
          <p class="onb__ecert-note">{{ t("onboarding.ecertNote") }}</p>
        </div>

        <v-expansion-panels v-model="detailsOpen" variant="accordion" class="onb__more">
          <v-expansion-panel :title="t('onboarding.moreDetails')" value="open">
            <v-expansion-panel-text>
              <div class="onb__form">
                <v-text-field
                  v-model="contactName"
                  :label="t('onboarding.contactName')"
                  variant="outlined"
                  density="compact"
                  autocomplete="name"
                  :disabled="busy"
                  hide-details
                />
                <v-text-field
                  v-model="contactPhone"
                  :label="t('onboarding.contactPhone')"
                  type="tel"
                  variant="outlined"
                  density="compact"
                  autocomplete="tel"
                  inputmode="tel"
                  :disabled="busy"
                  hide-details
                />
                <v-select
                  v-model="province"
                  :items="provinces"
                  :label="t('onboarding.province')"
                  variant="outlined"
                  density="compact"
                  clearable
                  :disabled="busy"
                  hide-details
                />
              </div>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>

        <v-btn
          type="submit"
          color="primary"
          size="large"
          block
          class="onb__submit"
          :loading="busy"
          :disabled="!canSubmit"
          >{{ hasCredentials ? t("onboarding.submitVerified") : t("onboarding.submit") }}</v-btn
        >
      </form>

      <p class="onb__foot">{{ t("onboarding.footNote") }}</p>
      <v-btn class="onb__switch" variant="text" size="small" block @click="session.signOut()">{{
        t("auth.differentAccountSignIn")
      }}</v-btn>
    </template>
  </PreAuthPage>
</template>

<script lang="ts">
/**
 * DEV-937 + DEV-938 — the one screen between an Auth0 sign-in and a working farm.
 *
 * Reached only from `status === "needs-org"`: signed in, no organisation, and
 * this tenant app accepts sign-ups. On any other app an orgless visitor still
 * lands on NoAccess — a stranger must not be able to mint an organisation inside
 * a white-label customer's tenant.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * TWO ROUTES IN, one screen.
 *
 *   with eCert credentials → the backend drives AgriOps with THEM, reads which
 *     business the login can edit, and claims or creates the matching
 *     organisation. This is the identity check: nobody but a director of that
 *     farm holds that login.
 *   without → a fresh, unverified organisation. Connecting AgriOps later is
 *     what verifies it.
 *
 * The credentials are NEVER mandatory. A grower who does not have their eCert
 * login to hand must still be able to get in — turning them away at the door to
 * enforce verification loses the user and verifies nothing.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * ⚠️ The business identity is never sent from here. `chosenBusinessId` only
 * SELECTS among businesses the backend already proved this login can edit; the
 * business id itself always comes from the server-side session. A
 * client-supplied one would let anyone claim any organisation with no members.
 *
 * ⚠️ The password lives in a ref for the length of one request and is cleared
 * the moment it is no longer needed. It is never persisted, never logged, and
 * the backend does not store it either (DEV-940).
 *
 * PAC-1374: on a WMS front door (`tenant.signupForm === "COMPANY"`) the body
 * is CompanySignupForm instead — legal name, registration/VAT, country, base
 * currency — and success lands in WMS on "add your first site". The AgriOps
 * step belongs to the farm form only.
 */
import { computed, defineComponent, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useSessionStore } from "@/stores/session";
import type { AgriOpsCandidate, AgriOpsSignupResult } from "@/auth/session";
import LogoTile from "@/shell/LogoTile.vue";
import PreAuthPage from "@/shell/PreAuthPage.vue";
import CompanySignupForm from "@/shell/views/CompanySignupForm.vue";

const ZA_PROVINCES = [
  "Eastern Cape",
  "Free State",
  "Gauteng",
  "KwaZulu-Natal",
  "Limpopo",
  "Mpumalanga",
  "Northern Cape",
  "North West",
  "Western Cape",
];

type Phase = "form" | "verifying" | "choose" | "review";

export default defineComponent({
  name: "Onboarding",
  components: { CompanySignupForm, LogoTile, PreAuthPage },
  setup() {
    const { t } = useI18n();
    const session = useSessionStore();

    const farmName = ref("");
    const contactName = ref("");
    const contactPhone = ref("");
    const province = ref<string | null>(null);
    const username = ref("");
    const password = ref("");
    const showPassword = ref(false);
    const detailsOpen = ref<string | undefined>(undefined);

    // PAC-1388: a company asked for on /signup that went to review at the
    // first sign-in arrives with its reference already — show the panel.
    const pendingReview = session.signupReviewReference;
    const phase = ref<Phase>(pendingReview === null ? "form" : "review");
    const busy = ref(false);
    const error = ref<string | null>(null);
    const reference = ref<string | null>(
      pendingReview === null ? null : pendingReview || t("onboarding.company.noReference"),
    );
    const candidates = ref<AgriOpsCandidate[]>([]);

    const hasCredentials = computed(() => !!username.value.trim() && !!password.value);
    const canSubmit = computed(() => farmName.value.trim().length > 1 && !busy.value);

    // PAC-1374: the form is the tenant app's choice. Anything but an explicit
    // "COMPANY" (an older backend sends nothing) is the farm form — Boer as before.
    const isCompany = computed(() => session.tenant?.signupForm === "COMPANY");

    /** A company review always shows the panel, even if the task (and so the reference) could not be written. */
    function onCompanyReview(companyRef: string): void {
      reference.value = companyRef || t("onboarding.company.noReference");
      phase.value = "review";
    }

    const farm = () => ({
      farmName: farmName.value.trim(),
      contactName: contactName.value.trim() || undefined,
      contactPhone: contactPhone.value.trim() || undefined,
      province: province.value || undefined,
    });

    async function submit(chosenBusinessId?: string): Promise<void> {
      if (busy.value) return;
      if (!chosenBusinessId && !canSubmit.value) return;
      busy.value = true;
      error.value = null;
      const verified = hasCredentials.value;
      if (verified) phase.value = "verifying";

      try {
        const result = verified
          ? await session.completeSignupWithAgriOps({
              ...farm(),
              username: username.value.trim(),
              password: password.value,
              chosenBusinessId,
            })
          : await session.completeSignup(farm());

        // The plain route returns no candidates, so narrow on the flag rather
        // than trusting a structural `in` check to carry the type across.
        if (verified && (result as AgriOpsSignupResult).needsChoice) {
          candidates.value = (result as AgriOpsSignupResult).candidates;
          phase.value = "choose";
          return;
        }
        if (result.outcome === "NEEDS_REVIEW") {
          reference.value = result.reference;
          phase.value = "review";
          // Nothing more will be sent; do not keep a working credential around.
          password.value = "";
          return;
        }
        // CREATED / CLAIMED — resolveOrgAccess already moved the session on and
        // App.vue swaps this view out. Clear the credential on the way past.
        password.value = "";
      } catch (e) {
        // The server writes these for the grower: "AgriOps did not accept the
        // login", "please confirm your email address first", a rate limit. All
        // more useful than a generic failure.
        error.value =
          (e as Error)?.message?.replace(/^POST \S+ failed: \d+\s*/, "") || t("onboarding.failed");
        // Back to the form so they can correct it — never stranded mid-flow.
        phase.value = candidates.value.length ? "choose" : "form";
      } finally {
        busy.value = false;
      }
    }

    const headTitle = computed(() => {
      if (isCompany.value) return t("onboarding.company.title");
      if (phase.value === "choose") return t("onboarding.chooseTitle");
      if (phase.value === "review") return t("onboarding.reviewTitle");
      return t("onboarding.title");
    });
    const headSubtitle = computed(() => {
      if (isCompany.value) return t("onboarding.company.subtitle", { name: session.tenant?.name || "Boer" });
      if (phase.value === "choose") return t("onboarding.chooseSubtitle");
      if (phase.value === "review") return "";
      return t("onboarding.subtitle", { name: session.tenant?.name || "Boer" });
    });

    return {
      t,
      session,
      farmName,
      contactName,
      contactPhone,
      province,
      username,
      password,
      showPassword,
      detailsOpen,
      provinces: ZA_PROVINCES,
      phase,
      busy,
      error,
      reference,
      candidates,
      hasCredentials,
      canSubmit,
      submit,
      headTitle,
      headSubtitle,
      isCompany,
      onCompanyReview,
      tenantName: computed(() => session.tenant?.name || "Boer"),
      branding: computed(() => session.tenant?.branding ?? null),
      email: computed(() => session.user?.email || null),
    };
  },
});
</script>

<style scoped>
.onb__form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.onb__ecert {
  border: 1px solid rgba(var(--v-theme-primary), 0.28);
  background: rgba(var(--v-theme-primary), 0.04);
  border-radius: 10px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.onb__ecert-head {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 650;
  font-size: 0.86rem;
}
.onb__ecert-blurb,
.onb__ecert-note {
  font-size: 0.73rem;
  line-height: 1.5;
  color: var(--sh-text-60);
  margin: 0;
}
.onb__ecert-field {
  margin: 0;
}
.onb__more {
  box-shadow: none;
}
.onb__submit {
  margin-top: 4px;
  font-weight: 650;
}
.onb__foot {
  font-size: 0.72rem;
  color: var(--sh-text-60);
  margin: 14px 0 0;
  text-align: center;
  line-height: 1.5;
}
.onb__switch {
  margin-top: 6px;
  font-size: 12px;
  color: var(--sh-text-60);
}
.onb__wait {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 26px 8px;
  text-align: center;
}
.onb__wait-title {
  font-weight: 650;
  margin: 4px 0 0;
}
.onb__wait-body {
  font-size: 0.78rem;
  color: var(--sh-text-60);
  margin: 0;
  max-width: 30ch;
  line-height: 1.55;
}
.onb__choose-lead {
  font-size: 0.8rem;
  color: var(--sh-text-60);
  margin: 0 0 10px;
  line-height: 1.55;
}
.onb__choice-name {
  font-weight: 600;
}
.onb__choice-ref {
  display: block;
  font-size: 0.7rem;
  color: var(--sh-text-60);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}
.onb__review {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 16px;
  border-radius: 10px;
  background: rgba(var(--v-theme-info), 0.08);
}
.onb__review-title {
  font-weight: 650;
  margin: 0 0 4px;
}
.onb__review-body {
  font-size: 0.8rem;
  line-height: 1.55;
  margin: 0;
  color: var(--sh-text-60);
}
.onb__ref {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  margin: 10px 0 0;
}
</style>
